import DataTable from '../../reusable/DataTable/DataTable';
import SymbolFlag from '../../reusable/SymbolFlag/SymbolFlag';
import InfoTip from '../../reusable/InfoTip/InfoTip';
import { NA, phaseLabel, signedUsd, usd } from '../../utilities/format';
import { t } from '../../i18n/i18n';

const icon = (name) => ({ '--icon': `url(/icons/${name}.svg)` });

/** "AUDCAD.pro" is the broker's symbol; the flag and the badge want "AUDCAD". */
const bare = (value) => String(value ?? '').split('.')[0];

/** `instrument_breakdown` arrives pre-formatted, so tone comes off the sign. */
const toneOf = (value) => (String(value ?? '').trim().startsWith('-') ? 'down' : 'up');

const asPct = (v) => (v == null || !Number.isFinite(Number(v)) ? NA : `${Number(v)}%`);
const asHour = (h) => (Number.isFinite(Number(h)) ? `${String(h).padStart(2, '0')}:00` : NA);
/** `one` / `many` are dictionary keys carrying `{{n}}` — "{{n}} trade", "{{n}} trades". */
const plural = (n, one, many) => (n === 1 ? t(one, { n }) : t(many, { n }));

/** The design draws four rails; the response splits them across two fields. */
const SESSION_ORDER = ['Asia', 'London', 'New York'];

const toSessions = (summary) => {
  const byName = new Map((summary?.by_session ?? []).map((row) => [row.session, row]));
  const rows = SESSION_ORDER.map((session) => {
    const row = byName.get(session);
    return {
      session,
      pnl: Number(row?.net_profit ?? 0),
      note: row ? plural(row.trades, '{{n}} trade', '{{n}} trades') : 'No trades',
    };
  });
  if (summary?.overlap_net != null) {
    rows.push({ session: 'Overlap', pnl: Number(summary.overlap_net), note: 'London / NY overlap' });
  }
  // Rails read as magnitudes against the heaviest session, not as a P&L sign.
  const max = Math.max(...rows.map((r) => Math.abs(r.pnl)), 0);
  return rows.map((r) => ({ ...r, pct: max ? (Math.abs(r.pnl) / max) * 100 : 0 }));
};

const toSessionStats = (summary, totalTrades) => [
  {
    label: 'Best hour',
    value: asHour(summary?.best_hour?.hour),
    note: summary?.best_hour?.session ?? NA,
  },
  {
    label: 'Worst hour',
    value: asHour(summary?.worst_hour?.hour),
    note: summary?.worst_hour?.session ?? NA,
  },
  {
    label: 'Avg trades / day',
    value: summary?.avg_trades_per_day ?? NA,
    note: plural(summary?.trading_days ?? 0, 'Across {{n}} day', 'Across {{n}} days'),
  },
  {
    label: 'Hold overnight',
    value: asPct(summary?.hold_overnight?.percentage),
    note: t('{{count}} of {{total}} trades', {
      count: summary?.hold_overnight?.count ?? 0,
      total: totalTrades ?? 0,
    }),
  },
];

/** The rail is the win rate, which is also what the note quotes. */
const toBias = (d) => [
  {
    side: 'Long',
    pnl: d?.long_profit,
    note: t('{{trades}} · {{rate}}% WR', {
      trades: plural(d?.long_trades ?? 0, '{{n}} trade', '{{n}} trades'),
      rate: d?.long_win_rate ?? 0,
    }),
    pct: Number(d?.long_win_rate ?? 0),
  },
  {
    side: 'Short',
    pnl: d?.short_profit,
    note: t('{{trades}} · {{rate}}% WR', {
      trades: plural(d?.short_trades ?? 0, '{{n}} trade', '{{n}} trades'),
      rate: d?.short_win_rate ?? 0,
    }),
    pct: Number(d?.short_win_rate ?? 0),
  },
];

const toRisk = (r) => [
  { label: 'Avg risk / trade', value: asPct(r?.avg_risk_per_trade), note: 'Of account equity' },
  { label: 'Largest risk', value: asPct(r?.largest_risk), note: 'Single trade' },
  { label: 'Margin peak', value: asPct(r?.margin_peak), note: 'Used of free margin' },
  { label: 'Slippage avg', value: r?.slippage_avg ?? NA, note: 'Pips vs requested' },
  { label: 'Fill quality', value: asPct(r?.fill_quality), note: 'Within 1 pip' },
  {
    label: 'Partial closes',
    value: r?.partial_closes?.count ?? NA,
    note: t('{{pct}}% of trades', { pct: r?.partial_closes?.percentage ?? 0 }),
  },
  { label: 'BE moves', value: r?.be_moves ?? NA, note: 'Stop to break-even' },
  { label: 'Revenge trades', value: r?.revenge_trades ?? NA, note: 'Flagged by rules' },
];

const known = (v) => v != null && v !== '' && Number.isFinite(Number(v));
const money = (v) => usd(v, { decimals: 2 });
const fixed2 = (v) => (known(v) ? Number(v).toFixed(2) : NA);

/** `4` → "4 wins", `-2` → "2 losses"; a string the API already worded passes through. */
const asStreak = (v) => {
  if (!known(v)) return v || NA;
  const n = Number(v);
  return n < 0 ? plural(-n, '{{n}} loss', '{{n}} losses') : plural(n, '{{n}} win', '{{n}} wins');
};

/** The session that netted most, for when the statistics don't name one. */
const bestSession = (summary) =>
  [...(summary?.by_session ?? [])].sort((a, b) => Number(b.net_profit) - Number(a.net_profit))[0]
    ?.session;

/**
 * The Advanced statistics grid — 1:10457, read row by row across three columns.
 * `tone`: 'signed' is a P&L that reads green or red with its sign; 'loss' is a
 * cost that only turns red once it is negative. Everything else is plain.
 * NEEDS PINNING: the keys past the Simple twelve (and `wins` / `losses`, which
 * the public profile's statistics use) are named in the backend's snake_case
 * but haven't been seen in an Advanced response yet — any that differ read N/A.
 */
const toAdvancedStats = (s, data) => {
  const bias = data?.directional_bias;
  const worstDayVal =
    typeof s?.worst_day === 'object' && s?.worst_day !== null
      ? s?.worst_day?.net_profit
      : s?.worst_day;
  return [
    { label: 'Total trades', def: 'total_trades', value: s.total_trades ?? NA },
    {
      label: 'Wins / losses', def: ['wins', 'losses'],
      value: s.wins != null || s.losses != null ? `${s.wins ?? 0} / ${s.losses ?? 0}` : NA,
    },
    { label: 'Net profit', def: 'net_profit', raw: s.net_profit, value: signedUsd(s.net_profit, { decimals: 2 }), tone: 'signed' },

    { label: 'Average win', def: 'average_win', value: money(s.average_win) },
    { label: 'Average loss', def: 'average_loss', raw: s.average_loss, value: money(s.average_loss), tone: 'loss' },
    { label: 'Expectancy', def: 'expectancy', value: money(s.expectancy) },

    { label: 'Largest win', def: 'largest_win', value: money(s.largest_win) },
    { label: 'Worst day', def: 'worst_day', raw: worstDayVal, value: signedUsd(worstDayVal, { decimals: 2 }), tone: 'signed' },
    { label: 'Max drawdown', def: 'max_drawdown', value: known(s.max_drawdown) ? `${fixed2(s.max_drawdown)}%` : NA },

    { label: 'Recovery factor', def: 'recovery_factor', value: fixed2(s.recovery_factor) },
    { label: 'Sharpe ratio', def: 'sharpe_ratio', value: fixed2(s.sharpe_ratio) },
    { label: 'Average hold', def: 'average_hold', value: s.average_hold ?? NA },

    // `{ session, net, trades }` in the response — the name is what the cell shows.
    {
      label: 'Best session', def: 'best_session',
      value: s.best_session?.session ?? bestSession(data?.session_summary) ?? NA,
    },
    { label: 'Current streak', def: 'current_streak', value: asStreak(s.current_streak) },
    { label: 'Average lots', def: 'average_lots', value: fixed2(s.average_lots) },

    { label: 'Profit factor', def: 'profit_factor', value: fixed2(s.profit_factor) },
    { label: 'Sortino ratio', def: 'sortino_ratio', value: fixed2(s.sortino_ratio) },
    { label: 'Payoff ratio', def: 'payoff_ratio', value: fixed2(s.payoff_ratio) },

    // Directional bias already carries these three, so they aren't asked of `statistics`.
    { label: 'Long win rate', def: 'long_win_rate', value: asPct(bias?.long_win_rate) },
    { label: 'Short win rate', def: 'short_win_rate', value: asPct(bias?.short_win_rate) },
    {
      label: 'Long / short', def: 'long_short',
      value: bias ? `${bias.long_trades ?? 0} / ${bias.short_trades ?? 0}` : NA,
    },

    { label: 'Commission paid', def: 'commission_paid', raw: s.commission_paid, value: money(s.commission_paid), tone: 'loss' },
    { label: 'Swap paid', def: 'swap_paid', raw: s.swap_paid, value: money(s.swap_paid), tone: 'loss' },
    { label: 'Avg R-multiple', def: 'avg_r_multiple', value: known(s.avg_r_multiple) ? `${fixed2(s.avg_r_multiple)}R` : NA },

    { label: 'Max consecutive wins', def: 'max_consecutive_wins', value: s.max_consecutive_wins ?? NA },
    { label: 'Max consecutive losses', def: 'max_consecutive_losses', value: s.max_consecutive_losses ?? NA },
    { label: 'Time in market', def: 'time_in_market', value: asPct(s.time_in_market) },

    { label: 'Edge per trade', def: 'edge_per_trade', raw: s.edge_per_trade, value: signedUsd(s.edge_per_trade, { decimals: 2 }), tone: 'signed' },
  ];
};

const toneClass = ({ tone, raw }) => {
  if (!tone || !known(raw) || Number(raw) === 0) return '';
  if (Number(raw) < 0) return ' is-down';
  return tone === 'signed' ? ' is-up' : '';
};

/**
 * The backend's statistic definitions — `/v2/account-metrics/statistics/
 * definitions/` answers `{ definitions: [{ key, description }] }` — as a
 * `key → description` lookup. Anything else (still loading, failed) is an
 * empty one, so labels simply go without their (i).
 */
export const toDefinitionMap = (response) =>
  Object.fromEntries(
    (Array.isArray(response?.definitions) ? response.definitions : [])
      .filter((d) => d?.key && d?.description)
      .map((d) => [d.key, d.description])
  );

/**
 * A statistic's label with an (i) that explains it, in the backend's words.
 * `def` is the definition key — or several, for a cell that shows more than
 * one figure ("Wins / losses"). No description on record, no (i).
 */
export function StatLabel({ label, def, definitions }) {
  const lines = (Array.isArray(def) ? def : [def])
    .map((key) => definitions?.[key])
    .filter(Boolean);
  if (!lines.length) return t(label);
  return (
    <span className="am-stat-label">
      <span className="am-stat-label__text">{t(label)}</span>
      <InfoTip
        className="am-limit__help"
        glyph="i"
        label={t('What is {{label}}?', { label: t(label) })}
        title={t(label)}
      >
        {lines.map((line) => (
          <span key={line}>{line}</span>
        ))}
      </InfoTip>
    </span>
  );
}

/**
 * Advanced's Statistics card — 1:10443. Simple's twelve-cell card becomes a
 * ruled three-column ledger with Volume by symbol beneath it.
 */
/**
 * `highWaterMark` — a trailing account's highest balance, the one its loss
 * limit trails (N111). It lives on the objectives record, not on
 * `statistics`, so the page hands it in; null on a fixed-limit account.
 */
export function AdvancedStatistics({ data, stage, loading = false, highWaterMark = null, definitions = null }) {
  const statistics =
    data?.statistics && Object.keys(data.statistics).length ? data.statistics : null;
  const volume = data?.volume_by_symbol ?? [];
  const phase = phaseLabel(statistics?.phase ?? stage);
  const defs = toDefinitionMap(definitions);

  return (
    <div className="am-card am-stats am-stats--advanced">
      <header className="am-card__head">
        <span className="am-card__title">
          <span className="icon am-card__icon" style={icon('metrics-statistics')} />
          {t('Statistics')}
        </span>
        <span className="am-card__meta">
          {[
            statistics?.total_trades == null
              ? NA
              : plural(statistics.total_trades, '{{n}} trade', '{{n}} trades'),
            phase && phase !== NA ? t(String(phase)).toLowerCase() : null,
            t('full breakdown'),
          ]
            .filter(Boolean)
            .join(' · ')}
        </span>
      </header>

      {!statistics ? (
        <p className="am-stats__empty">
          {loading ? t('Loading…') : t('No statistics came back for this account.')}
        </p>
      ) : (
        <dl className="am-ledger">
          {[
            ...toAdvancedStats(statistics, data),
            ...(highWaterMark != null
              ? [{ label: 'High-water mark', value: money(highWaterMark) }]
              : []),
          ].map((stat) => (
            <div className="am-ledger__cell" key={stat.label}>
              <dt className="am-ledger__label">
                <StatLabel label={stat.label} def={stat.def} definitions={defs} />
              </dt>
              {/* An unpinned key that turns out to be an object reads N/A, not a crash. */}
              <dd className={`am-ledger__value${toneClass(stat)}`}>
                {stat.value !== null && typeof stat.value === 'object' ? NA : stat.value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="am-card__rule" />

      <div className="am-card__head am-card__head--plain">
        <span className="am-card__meta">{t('Volume by symbol')}</span>
        <span className="am-card__meta">
          {plural(volume.length, '{{n}} asset', '{{n}} assets')} · {t('share of closed trades')}
        </span>
      </div>

      {volume.length === 0 ? (
        <span className="am-card__meta">{t('No volume yet')}</span>
      ) : (
        <div className="am-mix am-mix--grid">
          {volume.map((v) => (
            <div className="am-mix__row" key={v.symbol}>
              <span className="am-mix__id">
                <SymbolFlag symbol={bare(v.symbol)} />
                <span className="am-mix__label">{bare(v.symbol)}</span>
              </span>
              <Rail pct={Number(v.percentage)} />
              <span className="am-mix__pct">
                {known(v.percentage) ? `${Math.round(Number(v.percentage))}%` : NA}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** label / value / note cell, shared by both stat rows — 1:10987, 1:11364. */
const Stat = ({ label, value, note }) => (
  <div className="am-metric">
    <span className="am-metric__label">{t(label)}</span>
    <span className="am-metric__value">{value}</span>
    <span className="am-metric__note">{t(note)}</span>
  </div>
);

/** The blue pill rail every row in these sections shares. */
const Rail = ({ pct }) => (
  <span className="am-rail">
    <span className="am-rail__fill" style={{ width: `${Math.max(0, Math.min(pct, 100))}%` }} />
  </span>
);

/** Every figure here is a pre-formatted string bar `trades`. Widths are 1:11120's fr tracks. */
const columns = [
  {
    key: 'instrument',
    header: 'Instrument',
    width: 1.6,
    tone: () => 'primary',
    render: (r) => (
      <span className="am-instruments__symbol">
        <SymbolFlag symbol={bare(r.instrument)} />
        {bare(r.instrument)}
      </span>
    ),
  },
  { key: 'trades', header: 'Trades', width: 0.7, sortable: true },
  { key: 'winrate', header: 'Win rate', width: 0.8, sortable: true },
  {
    key: 'netpnl',
    header: 'Net PnL',
    width: 1,
    tone: (r) => toneOf(r.netpnl),
    sortValue: (r) => Number(String(r.netpnl).replace(/[^0-9.-]/g, '')),
    sortable: true,
  },
  { key: 'avghold', header: 'Avg hold', width: 0.8 },
  {
    key: 'expectancy',
    header: 'Expectancy',
    width: 1,
    // Only a losing expectancy is tinted; a positive one stays body grey — 1:11151.
    tone: (r) => (toneOf(r.expectancy) === 'down' ? 'down' : null),
    sortValue: (r) => Number(String(r.expectancy).replace(/[^0-9.-]/g, '')),
    sortable: true,
  },
];

/** The Advanced-only block — 1:10931. Simple stops at Account information. */
export default function AdvancedSections({ data, loading = false }) {
  const sessions = toSessions(data?.session_summary);
  const sessionStats = toSessionStats(data?.session_summary, data?.statistics?.total_trades);
  const bias = toBias(data?.directional_bias);
  const assetMix = [...(data?.asset_class_mix ?? [])].sort(
    (a, b) => Number(b.percentage) - Number(a.percentage)
  );
  const instruments = data?.instrument_breakdown ?? [];
  const riskStats = toRisk(data?.risk_execution);

  return (
    <>
      {/* ---- session performance + directional bias — 1:10932 ---- */}
      <section className="am-row am-row--sessions">
        <div className="am-card am-sessions">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('metrics-session')} />
              {t('Session performance')}
            </span>
            <span className="am-card__meta">{t('PnL by trading session')}</span>
          </header>

          <div className="am-sessions__list">
            {sessions.map((s) => (
              <div className="am-sessions__row" key={s.session}>
                <span className="am-sessions__name">{t(s.session)}</span>
                <Rail pct={s.pct} />
                <span className="am-sessions__result">
                  <span className={`am-sessions__value is-${s.pnl < 0 ? 'down' : 'up'}`}>
                    {signedUsd(s.pnl)}
                  </span>
                  <span className="am-sessions__note">{t(s.note)}</span>
                </span>
              </div>
            ))}
          </div>

          <div className="am-card__rule" />

          <div className="am-metric-row am-metric-row--wide">
            {sessionStats.map((s) => (
              <Stat key={s.label} {...s} />
            ))}
          </div>
        </div>

        <div className="am-card am-bias">
          <header className="am-card__head">
            <span className="am-card__title">
              <span className="icon am-card__icon" style={icon('metrics-direction')} />
              {t('Directional bias')}
            </span>
            <span className="am-card__meta">{t('Long vs short')}</span>
          </header>

          <div className="am-bias__split">
            {bias.map((b) => (
              <div className="am-bias__side" key={b.side}>
                <span className="am-metric__label">{t(b.side)}</span>
                <span className={`am-bias__value is-${Number(b.pnl) < 0 ? 'down' : 'up'}`}>
                  {signedUsd(b.pnl)}
                </span>
                <span className="am-metric__note">{b.note}</span>
                <Rail pct={b.pct} />
              </div>
            ))}
          </div>

          <div className="am-card__rule" />

          {/* Asset classes by share of trades, largest first — 1:10931, AC1-I120. */}
          <div className="am-card__head am-card__head--plain">
            <span className="am-card__meta">{t('Asset class mix')}</span>
            <span className="am-card__meta">{t('By trade count')}</span>
          </div>

          <div className="am-mix">
            {assetMix.length === 0 && <span className="am-card__meta">{t('No trades yet')}</span>}
            {assetMix.map((a) => (
              <div className="am-mix__row" key={a.asset_class}>
                <span className="am-mix__id">
                  {/* "Indices" → IND, "FX" → FX — the design's three-letter tiles. */}
                  <span className="am-mix__badge">{String(a.asset_class ?? '').slice(0, 3)}</span>
                  <span className="am-mix__label">{t(a.asset_class)}</span>
                </span>
                <Rail pct={Number(a.percentage)} />
                <span className="am-mix__pct">{asPct(a.percentage)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---- instrument breakdown — 1:11108 ---- */}
      <DataTable
        className="am-instruments"
        title="Instrument breakdown"
        count={false}
        icon="metrics-instruments"
        columns={columns}
        rows={instruments}
        getRowId={(r) => r.instrument}
        minWidth={576}
        pageSize={Math.max(instruments.length, 1)}
        pagination={false}
        loading={loading}
        emptyMessage="No instruments traded on this account"
        actions={
          <span className="am-card__meta">
            {plural(instruments.length, '{{n}} asset', '{{n}} assets')} · {t('advanced ledger')}
          </span>
        }
      />

      {/* ---- risk & execution — 1:11351 ---- */}
      <section className="am-card am-risk-exec">
        <header className="am-card__head">
          <span className="am-card__title">
            <span className="icon am-card__icon" style={icon('metrics-risk')} />
            {t('Risk & execution')}
          </span>
          <span className="am-card__meta">{t('Advanced risk controls')}</span>
        </header>

        <div className="am-card__rule" />

        <div className="am-metric-row">
          {riskStats.map((s) => (
            <Stat key={s.label} {...s} />
          ))}
        </div>
      </section>
    </>
  );
}
